import { RequireAccess } from '@/components/require-access'
import { roleEnum } from '@jobilla/nexus-sdk'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorErrorBoundary } from '../../../editor/error-handling'
import { Button } from '../../../rapid-ui'
import { SettingsNav } from '../settings-nav'
import { SettingsHeading } from '../shared'
import { OrganizationAccess } from './organization/organization-access'
import { OrganizationInvitationsTable } from './organization/organization-invitations-table'
import { OrganizationInviteDialog } from './organization/organization-invite-dialog'
import { OrganizationMembersTable } from './organization/organization-members-table'
import { OrganizationPagination } from './organization/organization-pagination'
import { useOrganizationViewModel } from './organization/organization.vm'
import { RemoveMemberDialog } from './organization/remove-member-dialog'
import { RevokeInvitationDialog } from './organization/revoke-invitation-dialog'

type TabView = 'members' | 'invites'

export function SettingsMembers() {
	const { t } = useTranslation('common')
	const vm = useOrganizationViewModel()
	const [activeTab, setActiveTab] = useState<TabView>('members')

	return (
		<SettingsNav active="members">
			<SettingsHeading>{t('settingsNav.members')}</SettingsHeading>
			<p className="max-w-[600px] text-gray-600">
				{t('members.description')}
			</p>

			<RequireAccess
				role={[roleEnum.admin, roleEnum.jobillan]}
				fallback={
					<p className="text-red-600">
						{t('organization.noPermission')}
					</p>
				}
			>
				<div className="max-w-[800px]">
					<div className="mb-6 flex items-center justify-between">
						<div className="relative inline-flex rounded-lg bg-gray-100 p-1 gap-1">
							<button
								type="button"
								onClick={() => setActiveTab('members')}
								className={`relative z-10 px-6 py-2.5 text-sm font-medium rounded-md ${
									activeTab === 'members'
										? 'text-gray-900'
										: 'text-gray-600 hover:text-gray-800'
								}`}
							>
								{activeTab === 'members' && (
									<motion.div
										layoutId="activeTab"
										className="absolute inset-0 bg-white shadow-sm rounded-md"
										transition={{
											type: 'spring',
											stiffness: 500,
											damping: 40,
											mass: 0.8,
										}}
									/>
								)}
								<span className="relative z-10">
									{t('organization.members')}
									{vm.totalUsers > 0 && (
										<span className="ml-2 text-xs text-gray-500">
											({vm.totalUsers})
										</span>
									)}
								</span>
							</button>
							<button
								type="button"
								onClick={() => setActiveTab('invites')}
								className={`relative z-10 px-6 py-2.5 text-sm font-medium rounded-md ${
									activeTab === 'invites'
										? 'text-gray-900'
										: 'text-gray-600 hover:text-gray-800'
								}`}
							>
								{activeTab === 'invites' && (
									<motion.div
										layoutId="activeTab"
										className="absolute inset-0 bg-white shadow-sm rounded-md"
										transition={{
											type: 'spring',
											stiffness: 500,
											damping: 40,
											mass: 0.8,
										}}
									/>
								)}
								<span className="relative z-10">
									{t('organization.invites')}
									{vm.totalInvitations > 0 && (
										<span className="ml-2 text-xs text-gray-500">
											({vm.totalInvitations})
										</span>
									)}
								</span>
							</button>
						</div>

						<Button onClick={() => vm.setShowInviteDialog(true)} size={'tiny'}>
							{t('organization.invite')}
						</Button>
					</div>

					<div className="relative">
						<AnimatePresence mode="wait" initial={false}>
							{activeTab === 'members'
								? (
									<motion.div
										key="members"
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: 20 }}
										transition={{
											duration: 0.2,
											ease: 'easeInOut',
										}}
									>
										<EditorErrorBoundary debugId={'settings-organization-members-table'}>
											<OrganizationMembersTable
												users={vm.users}
												isLoading={vm.status === 'pending'}
												isPlaceholderData={vm.isPlaceholderData}
												isSuccess={vm.isSuccess}
												isJobillan={vm.isJobillan}
												currentUserId={vm.currentUserId}
												onRoleChange={vm.handleRoleChange}
												onRemoveClick={vm.handleRemoveMemberClick}
											/>
										</EditorErrorBoundary>

										<OrganizationPagination
											currentPage={vm.currentPage}
											totalPages={vm.totalPages}
											totalUsers={vm.totalUsers}
											actualItemsOnPage={vm.actualItemsOnPage}
											itemsPerPage={vm.itemsPerPage}
											isPlaceholderData={vm.isPlaceholderData}
											onPreviousPage={vm.handlePreviousPage}
											onNextPage={vm.handleNextPage}
											onItemsPerPageChange={vm.handleItemsPerPageChange}
										/>
									</motion.div>
								)
								: (
									<motion.div
										key="invites"
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: 20 }}
										transition={{
											duration: 0.2,
											ease: 'easeInOut',
										}}
									>
										<EditorErrorBoundary debugId={'settings-organization-invitations-table'}>
											<OrganizationInvitationsTable
												invitations={vm.invitations}
												isLoading={vm.isLoadingInvitations}
												isPlaceholderData={vm.isPlaceholderInvitations}
												isSuccess={vm.isSuccessInvitations}
												onRevokeInvitation={vm.handleRevokeClick}
											/>
										</EditorErrorBoundary>
										<OrganizationPagination
											currentPage={vm.invitationsPage}
											totalPages={Math.ceil(vm.totalInvitations / vm.invitationsPerPage)}
											totalUsers={vm.totalInvitations}
											actualItemsOnPage={vm.invitations?.length ?? 0}
											itemsPerPage={vm.invitationsPerPage}
											isPlaceholderData={vm.isPlaceholderInvitations}
											onPreviousPage={vm.handleInvitationsPreviousPage}
											onNextPage={vm.handleInvitationsNextPage}
											onItemsPerPageChange={vm.handleInvitationsPerPageChange}
										/>
									</motion.div>
								)}
						</AnimatePresence>
					</div>
				</div>
			</RequireAccess>

			<OrganizationAccess />

			<OrganizationInviteDialog
				isOpen={vm.showInviteDialog}
				inviteEmails={vm.inviteEmails}
				inviteRole={vm.inviteRole}
				invitationErrors={vm.invitationErrors}
				isJobillan={vm.isJobillan}
				onClose={vm.handleCancelInvite}
				onEmailsChange={vm.setInviteEmails}
				onRoleChange={vm.setInviteRole}
				onSendInvites={vm.handleSendInvites}
			/>

			<RevokeInvitationDialog
				isOpen={vm.showRevokeDialog}
				email={vm.selectedInvitation?.email || ''}
				onClose={vm.handleCancelRevoke}
				onConfirm={vm.handleConfirmRevoke}
				isRevoking={vm.isRevokingInvitation}
			/>

			<RemoveMemberDialog
				isOpen={vm.showRemoveMemberDialog}
				name={vm.selectedMember?.name || ''}
				email={vm.selectedMember?.email || ''}
				onClose={vm.handleCancelRemoveMember}
				onConfirm={vm.handleConfirmRemoveMember}
				isRemoving={vm.isRemovingMember}
			/>
		</SettingsNav>
	)
}
